import { ClassName } from '../methodologyDeep/models';
import type { MethodologyUpdatePayload } from '../methodologyDeep/models';
import type { I18nMap, UUID } from '../app/basicModels';
import type { Characteristic, CharacteristicWithDefaultIndicator } from '../characteristic/models';
import type { ExtraDimension } from '../extraDimensions/models';
import type { Indicator } from '../indicator/models';
import type { Methodology } from '../methodology/models';
import type { Phenomenon, CloneCharacteristicsIndicatorsDTO, ClonedCharacteristicsIndicators } from '../phenomenon/models';
import type { PhenomenonCategory } from '../phenomenonCategory/models';

export const mapClonedIndicator = (indicator: Indicator & { org_id?: UUID | null }): Indicator => ({
  ...indicator,
  company_id: indicator.org_id ?? indicator.company_id
});

export const mapClonedCharacteristic = (characteristic: Characteristic & { org_id?: UUID | null }): Characteristic => ({
  ...characteristic,
  company_id: characteristic.org_id ?? characteristic.company_id
});

export const mapClonedPhenomenon = (phenomenon: Phenomenon): Phenomenon => ({
  ...phenomenon,
  characteristics_dto: phenomenon.characteristics ?? [],
  indicators_dto: phenomenon.indicators ?? []
});

export const mapPhenomenonEntitiesForClone = (items: CloneCharacteristicsIndicatorsDTO) => ({
  ...(items.workspace_id ? { workspace_id: items.workspace_id } : { org_id: items.company_id }),
  reference_phenomenon_id: items.reference_phenomenon_id,
  destiny_phenomenon_id: items.destiny_phenomenon_id,
  characteristic_ids: items.characteristic_ids,
  indicator_ids: items.indicator_ids
});

export const mapClonedPhenomenonEntities = (items: ClonedCharacteristicsIndicators): ClonedCharacteristicsIndicators => ({
  ...items,
  characteristics: items.characteristics.map(mapClonedCharacteristic),
  indicators: items.indicators.map(mapClonedIndicator)
});

export const mapMethodologyForClone = ({ methodology_dto: attributes }: MethodologyUpdatePayload) => ({
  name: attributes.name,
  description: attributes.description,
  crop_id: attributes.crop_id,
  default_methodology: attributes.default_methodology,
  season_ids: attributes.season_ids,
  season_property_ids: attributes.season_property_ids,
  season_field_ids: attributes.season_field_ids ?? attributes.season_area_ids
});

interface ConfigurationOwnership {
  company_id?: string | null;
  workspace_id?: string | null;
  is_canonical?: boolean;
}

const mapOwnership = ({ company_id, workspace_id, is_canonical }: ConfigurationOwnership) => ({
  ...(company_id ? { org_id: company_id } : {}),
  ...(workspace_id ? { workspace_id } : {}),
  ...(is_canonical !== undefined ? { is_canonical } : {})
});

export const mapPhenomenonCategoryForCreate = (category: PhenomenonCategory) => ({
  ...(category.id ? { id: category.id } : {}),
  name: category.name,
  description: category.description
});

export const mapPhenomenonCategoryForUpdate = (category: PhenomenonCategory) => ({
  name: category.name,
  description: category.description
});

export const mapPhenomenonForCreate = (phenomenon: Phenomenon) => ({
  ...(phenomenon.id ? { id: phenomenon.id } : {}),
  ...(phenomenon.parent_id ? { parent_id: phenomenon.parent_id } : {}),
  ...mapOwnership(phenomenon),
  name: phenomenon.name,
  description: phenomenon.description,
  short_description: phenomenon.short_description,
  cohese: phenomenon.cohese,
  category_id: phenomenon.category?.id
});

export const mapPhenomenonForUpdate = (phenomenon: Phenomenon) => ({
  name: phenomenon.name,
  description: phenomenon.description,
  short_description: phenomenon.short_description,
  cohese: phenomenon.cohese,
  category_id: phenomenon.category?.id
});

const mapInputDefinition = (inputDefinition: Characteristic['input_definition']) => {
  const { input_behaviours, ...attributes } = inputDefinition;

  return {
    ...attributes,
    ...(input_behaviours !== undefined ? { input_behaviors: input_behaviours } : {})
  };
};

const mapIndicatorExpression = (indicator: Indicator, characteristicId?: string) => {
  const expression = indicator.expression ?? {};
  const characteristicIds = expression.characteristics ?? [];
  const characteristics =
    characteristicId && !characteristicIds.includes(characteristicId) ? [...characteristicIds, characteristicId] : characteristicIds;

  return {
    ...expression,
    characteristics,
    ...(!expression.type && indicator.expression_type ? { type: indicator.expression_type } : {})
  };
};

const mapLocalizedStrings = (value?: I18nMap): I18nMap => {
  const translations = Object.entries(value?.localized_strings ?? {}).filter(([, text]) => typeof text === 'string');

  return translations.length ? { localized_strings: Object.fromEntries(translations) } : { localized_strings: { en: '' } };
};

const mapIndicatorContent = (indicator: Indicator, characteristicId?: string) => ({
  name: mapLocalizedStrings(indicator.name),
  description: mapLocalizedStrings(indicator.description),
  short_description: mapLocalizedStrings(indicator.short_description),
  uom: mapLocalizedStrings(indicator.uom),
  ...(indicator.decimal_places !== undefined ? { decimal_places: indicator.decimal_places } : {}),
  feature_flags: indicator.feature_flags,
  diagnostics: indicator.diagnostics,
  expression: mapIndicatorExpression(indicator, characteristicId)
});

export const mapCharacteristicForCreate = (characteristic: Characteristic | CharacteristicWithDefaultIndicator) => {
  const body = {
    ...(characteristic.id ? { id: characteristic.id } : {}),
    ...(characteristic.parent_id ? { parent_id: characteristic.parent_id } : {}),
    ...mapOwnership(characteristic),
    name: mapLocalizedStrings(characteristic.name),
    description: mapLocalizedStrings(characteristic.description),
    tip: mapLocalizedStrings(characteristic.tip),
    input_definition: mapInputDefinition(characteristic.input_definition),
    aliases_ids: characteristic.aliases_ids,
    phenomenon_id: characteristic.phenomenon_id,
    default_indicator_id: characteristic.default_indicator_id,
    crop_ids: characteristic.crop_ids
  };

  if ('default_indicator' in characteristic && characteristic.default_indicator) {
    return {
      ...body,
      default_indicator: {
        id: characteristic.default_indicator_id,
        ...mapIndicatorContent(characteristic.default_indicator, characteristic.id)
      }
    };
  }

  return body;
};

export const mapCharacteristicForUpdate = (characteristic: Characteristic) => ({
  name: mapLocalizedStrings(characteristic.name),
  description: mapLocalizedStrings(characteristic.description),
  tip: mapLocalizedStrings(characteristic.tip),
  input_definition: mapInputDefinition(characteristic.input_definition),
  aliases_ids: characteristic.aliases_ids,
  default_indicator_id: characteristic.default_indicator_id,
  crop_ids: characteristic.crop_ids
});

export const mapIndicatorForCreate = (indicator: Indicator) => ({
  ...(indicator.id ? { id: indicator.id } : {}),
  ...(indicator.parent_id ? { parent_id: indicator.parent_id } : {}),
  ...mapOwnership(indicator),
  ...mapIndicatorContent(indicator, indicator.characteristic_id),
  phenomenon_ids: indicator.phenomenon_ids
});

export const mapIndicatorForUpdate = (indicator: Indicator) => mapIndicatorContent(indicator);

export const mapExtraDimensionForCreate = (extraDimension: ExtraDimension) => ({
  ...(extraDimension.id ? { id: extraDimension.id } : {}),
  ...mapOwnership(extraDimension),
  name: extraDimension.name,
  description: extraDimension.description,
  required: extraDimension.required,
  values: extraDimension.values
});

export const mapExtraDimensionForUpdate = (extraDimension: ExtraDimension) => ({
  name: extraDimension.name,
  description: extraDimension.description,
  required: extraDimension.required,
  values: extraDimension.values
});

export const methodologyCreateHasLayout = (methodology: Methodology): boolean => {
  const layout = methodology.inspection_layout;
  const hasInspectionLayoutContent =
    !!layout &&
    ((layout.ordered_inspection_groups?.length ?? 0) > 0 ||
      (layout.independent_variables?.length ?? 0) > 0 ||
      (layout.characteristic_ids?.length ?? 0) > 0 ||
      (layout.indicator_ids?.length ?? 0) > 0 ||
      (layout.variables_validation?.length ?? 0) > 0);
  const hasAnalyticContextContent = (methodology.analytic_context_dto?.custom_indicator_dtos.length ?? 0) > 0;

  return hasInspectionLayoutContent || hasAnalyticContextContent || methodology.display_layout !== undefined;
};

export const mapMethodologyForCreate = (methodology: Methodology) => ({
  ...(methodology.id ? { id: methodology.id } : {}),
  ...(methodology.parent_id ? { parent_id: methodology.parent_id } : {}),
  ...mapOwnership(methodology),
  name: methodology.name,
  description: methodology.description,
  crop_id: methodology.crop_id,
  station_id: methodology.station_id,
  product_set_id: methodology.product_set_id,
  constants: methodology.constants,
  default_methodology: methodology.default_methodology,
  scouting_available_until: methodology.scouting_available_until,
  season_ids: methodology.season_ids,
  season_property_ids: methodology.season_property_ids,
  season_field_ids: methodology.season_field_ids ?? methodology.season_area_ids
});

const mapComponentType = (className: string): 'CHARACTERISTIC' | 'INDICATOR' => {
  const componentTypes: Record<string, 'CHARACTERISTIC' | 'INDICATOR'> = {
    [ClassName.CHARACTERISTIC]: 'CHARACTERISTIC',
    [ClassName.INDICATOR]: 'INDICATOR'
  };
  const componentType = componentTypes[className];

  if (!componentType) throw new Error(`Unsupported methodology component class: ${className}`);

  return componentType;
};

const mapInspectionLayout = (layout: Methodology['inspection_layout']) => {
  if (!layout) return undefined;

  return {
    id: layout.id,
    characteristic_ids: layout.characteristic_ids,
    indicator_ids: layout.indicator_ids,
    independent_variables: layout.independent_variables?.map(({ id, component_id, priority }) => ({ id, component_id, priority })),
    variables_validation: layout.variables_validation,
    ordered_inspection_groups: layout.ordered_inspection_groups.map(group => ({
      id: group.id,
      component_id: group.component_id,
      name: group.name,
      priority: group.priority,
      mandatory_photo: group.mandatory_photo,
      ordered_extra_dimensions: group.ordered_extra_dimensions.map(({ extra_dimension_id, priority }) => ({
        extra_dimension_id,
        priority
      })),
      ordered_categories: group.ordered_categories.map(category => ({
        id: category.id,
        component_id: category.component_id,
        priority: category.priority,
        ordered_phenomena: category.ordered_phenomenons.map(phenomenon => ({
          id: phenomenon.id,
          component_id: phenomenon.component_id,
          priority: phenomenon.priority,
          mandatory_photo: phenomenon.mandatory_photo,
          component_clusters: phenomenon.component_clusters,
          ordered_components: phenomenon.ordered_components.map(({ id, component_id, priority, class_name, aliases_ids }) => ({
            id,
            component_id,
            priority,
            ...(aliases_ids ? { aliases_ids } : {}),
            type: mapComponentType(class_name)
          }))
        }))
      }))
    }))
  };
};

const mapAnalyticContext = (analyticContext: Methodology['analytic_context_dto']) => {
  if (!analyticContext) return undefined;

  return {
    id: analyticContext.id,
    custom_indicators: analyticContext.custom_indicator_dtos
  };
};

export const mapMethodologyForUpdate = ({ methodology_dto: methodology }: MethodologyUpdatePayload) => ({
  name: methodology.name,
  description: methodology.description,
  crop_id: methodology.crop_id,
  station_id: methodology.station_id,
  parent_id: methodology.parent_id,
  product_set_id: methodology.product_set_id,
  constants: methodology.constants,
  default_methodology: methodology.default_methodology,
  scouting_available_until: methodology.scouting_available_until,
  season_ids: methodology.season_ids,
  season_property_ids: methodology.season_property_ids,
  season_field_ids: methodology.season_field_ids ?? methodology.season_area_ids,
  analytic_context: mapAnalyticContext(methodology.analytic_context_dto),
  inspection_layout: mapInspectionLayout(methodology.inspection_layout),
  display_layout: methodology.display_layout
});
